import { CompanyReviewsBlock } from 'cms-types'
import { FormattedReviewsList, LocalizedComponentProps, ReviewsWidget } from 'ui'
import { formatCompanyReviews } from 'ui/helpers/formatReviews'

import { logger } from '@/helpers/logger'

const REVIEWS_STORE = process.env.NEXT_PUBLIC_REVIEWS_STORE
const REVIEWS_API_URL = `${process.env.NEXT_PUBLIC_REVIEWS_API_URL}/merchant/reviews`
const REVIEWS_API_PARAMS = {
  store: REVIEWS_STORE,
  per_page: '30',
  order: 'desc',
}
const REVIEWS_REVALIDATE_SECONDS = 3600

const fetchCompanyReviewsPage = async (page: number) => {
  const params = new URLSearchParams({ ...REVIEWS_API_PARAMS, page: String(page) })

  const response = await fetch(`${REVIEWS_API_URL}?${params.toString()}`, {
    next: { revalidate: REVIEWS_REVALIDATE_SECONDS },
  })

  if (!response.ok) {
    throw new Error(`Reviews API responded with ${response.status}`)
  }

  return response.json()
}

const fetchValidatedCompanyReviews = async (
  locale: string,
  amount = 10,
  index = 1,
  previousValidatedReviews?: FormattedReviewsList,
): Promise<FormattedReviewsList> => {
  const validatedReviews = previousValidatedReviews ?? { ratings: {}, reviews: [] }

  try {
    const response = await fetchCompanyReviewsPage(index)
    if (response.reviews.length === 0) return validatedReviews

    const formattedResponse = formatCompanyReviews(response, locale)
    validatedReviews.ratings = formattedResponse.ratings
    validatedReviews.reviews = [
      ...validatedReviews.reviews,
      ...formattedResponse.reviews.filter(review => review.text !== ''),
    ]

    if (validatedReviews.reviews.length < amount)
      return fetchValidatedCompanyReviews(locale, amount, index + 1, validatedReviews)
  } catch (error) {
    logger.error('CompanyReviews', error)
  }

  return validatedReviews
}

export default async function CompanyReviews({
  title,
  reviewsText,
  fromText,
  verifiedCustomerText,
  locale,
}: LocalizedComponentProps<CompanyReviewsBlock>) {
  const reviews = await fetchValidatedCompanyReviews(locale)

  return (
    <ReviewsWidget
      reviews={reviews}
      reviewsUrl={`https://www.reviews.io/company-reviews/store/${REVIEWS_STORE}`}
      title={title}
      reviewsText={reviewsText}
      fromText={fromText}
      verifiedCustomerText={verifiedCustomerText}
    />
  )
}
